import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useRouter } from "next/router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ExternalLink } from "lucide-react";
import { Layout5050 } from "@/components/layouts/layout-50-50";

const resetSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ResetFormValues = z.infer<typeof resetSchema>;

export default function RequestResetPassword() {
  const router = useRouter();
  const { co } = router.query;
  const [submittedEmail, setSubmittedEmail] = useState<string>("");

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
  });

  const forgetMutation = useMutation({
    mutationFn: async (data: ResetFormValues) => {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/customer/auth/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username: data.email,
            companyId: parseInt(co as string),
          }),
        },
      );

      if (!response.ok) {
        const text = await response.text();
        let errorMessage = "Failed to request password reset";

        try {
          // Try to parse as JSON
          const errorData = JSON.parse(text) as { message: string };
          // Use the error message from the response
          errorMessage = errorData.message || errorMessage;
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (parseError) {
          // Fallback to text if parsing fails
          errorMessage =
            text || `Error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      return response;
    },
    onSuccess: (_, variables) => {
      setSubmittedEmail(variables.email);
    },
  });

  const onSubmit = (data: ResetFormValues) => {
    forgetMutation.mutate(data);
  };

  const isSubmitDisabled = forgetMutation.isPending || !form.formState.isValid;
  const cardInfo = !submittedEmail
    ? {
        title: "Reset your password",
        description:
          "Enter the email address associated with your account and we'll send you a link to reset your password.",
      }
    : {
        title: "Check your email",
        description: (
          <div className="flex flex-col gap-2">
            <p>
              If <strong>{submittedEmail}</strong> matches the email we have on
              file for your account, you will receive a link to reset your
              password.
            </p>

            <p>
              If you don't get an email within the next 5 minutes check your
              spam folder, or try again.
            </p>
          </div>
        ),
      };

  return (
    <Layout5050
      companyId={co ? Number(co) : null}
      cardInfo={cardInfo}
      variant="unbranded"
    >
      {!submittedEmail ? (
        <>
          {co ? (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="flex flex-col gap-2">
                          <FormLabel className="text-sm font-medium">
                            Email
                          </FormLabel>
                          <Input
                            placeholder="Enter your email address"
                            type="email"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {forgetMutation.isError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="break-words">
                      {forgetMutation.error?.message ||
                        "An unexpected error occurred"}
                    </AlertDescription>
                  </Alert>
                )}
                <Button
                  type="submit"
                  intent="action"
                  className="w-full bg-legacyPrimary"
                  disabled={isSubmitDisabled}
                >
                  {forgetMutation.isPending ? "Sending..." : "Send Reset Link"}
                </Button>

                <Button
                  variant="ghost"
                  className="w-full text-foreground-muted"
                  onClick={() => {
                    router.push(`/login/portal/${co}`);
                  }}
                >
                  Return to Login
                </Button>
              </form>
            </Form>
          ) : (
            <Alert variant="destructive" className="max-w-md my-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Missing Company ID</AlertDescription>
            </Alert>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <Button asChild variant="fill" className="w-full">
            <div className="flex gap-2">
              <ExternalLink size={16} />
              <a href="mailto:">Open Mail App</a>
            </div>
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setSubmittedEmail("");
              form.reset();
            }}
          >
            Try a Different Email Address
          </Button>
        </div>
      )}
    </Layout5050>
  );
}
